<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import IconButton from '@/components/shared/IconButton.vue';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import { eventBus } from '@/utils/eventBus';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import IconCircle, {
  type IIconCircle,
} from '@/components/shared/IconCircle.vue';
import { AlignmentTypes } from '@/graphql/shared-types';

interface Props {
  headerImage?: string;
  grayscaleImage?: boolean;
  iconCircle?: IIconCircle;
  scrollHeight?: number;
  duration?: number;
  title?: string;
  label?: string;
  titlePosition?: AlignmentTypes;
}

const props = withDefaults(defineProps<Props>(), {
  titlePosition: 'left',
});

const emit = defineEmits(['close']);

const showContent = ref<boolean>(false);
const showWrapper = ref<boolean>(true);
const scrollContainer = ref<HTMLElement | null>(null);
const contentContainer = ref<HTMLElement | null>(null);
const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

onMounted(() => {
  eventBus.on('triggerClose', close);
  showContent.value = true;

  requestAnimationFrame(() => {
    if (scrollContainer.value && contentContainer.value) {
      if (!props.scrollHeight) {
        const contentHeight = contentContainer.value?.clientHeight || 200;

        scrollContainer.value.scrollTop = contentHeight < 800 ? 300 : 800;
      } else {
        scrollContainer.value.scrollTop = props.scrollHeight;
      }
    }
  });

  if (props.duration) {
    timeoutId.value = setTimeout(() => {
      eventBus.emit('triggerClose');
    }, props.duration);
  }
});

onBeforeUnmount(() => {
  eventBus.off('triggerClose', close);
  if (timeoutId.value !== null) {
    clearTimeout(timeoutId.value);
  }
});

const close = () => {
  // Close wrapper overlay before animating the overlay itself down
  showContent.value = false;
  setTimeout(() => {
    showWrapper.value = false;
    emit('close');
  }, 400);
};

defineExpose({ close });
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-500 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-500 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showWrapper"
        data-no-pressable
        class="fixed inset-0 z-999 bg-[rgba(0,0,0,0.5)]"
      >
        <Transition
          enter-active-class="transition-transform duration-500 ease-out"
          enter-from-class="translate-y-full"
          enter-to-class="translate-y-0"
          leave-active-class="transition-transform duration-500 ease-in"
          leave-from-class="translate-y-0"
          leave-to-class="translate-y-full"
        >
          <div v-if="showContent" class="h-full flex flex-col transform">
            <div ref="scrollContainer" class="flex-1 overflow-y-scroll">
              <div class="backdrop bg-transparent min-h-300"></div>
              <div
                class="main-content bg-neutral-100 rounded-t-3xl p-12 min-h-500 shadow-small"
              >
                <div class="header relative" :class="{ 'mb-12': headerImage }">
                  <div
                    v-if="headerImage"
                    class="absolute left-1/2 -translate-x-1/2 -mt-37.5"
                  >
                    <img
                      :class="{
                        grayscale: grayscaleImage,
                      }"
                      :src="headerImage"
                      class="rounded-full w-60 h-60 border-8 border-white shadow-m object-cover bg-white"
                      v-optimized="{ src: headerImage }"
                    />

                    <IconCircle
                      v-if="iconCircle"
                      v-bind="iconCircle"
                      class="absolute right-0 -bottom-2"
                    />
                  </div>

                  <div class="flex justify-between items-center mb-8 text-xl">
                    <IconButton :icon="['far', 'times']" @click="close" />
                  </div>
                </div>

                <div ref="contentContainer">
                  <div class="flex justify-between">
                    <TitleBlock
                      v-if="title"
                      :text="title"
                      :alignment="titlePosition"
                      :label="label"
                    />
                    <slot v-if="$slots.tts" name="tts"></slot>
                  </div>

                  <slot name="content"></slot>
                </div>
              </div>

              <div
                v-if="$slots.actions"
                class="fixed left-0 right-0 mx-auto w-fit h-32 flex items-center justify-center"
                :style="{ bottom: '208px' }"
              >
                <slot name="actions" class="bg-red-200"></slot>
              </div>
            </div>

            <div
              class="border-t bg-neutral-100 border-neutral-300 w-full flex items-center justify-center shadow-large"
              :style="{ height: '168px' }"
            >
              <ButtonEl
                text="global.close"
                background="bg-neutral-0"
                textColor="text-neutral-1000"
                class="shadow-large border border-neutral-300"
                rounded="full"
                :icon="['far', 'fa-times']"
                icon-size="text-4xl"
                typography="h600"
                padding="px-6 py-8"
                @click="close()"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
* {
  z-index: 999;
}

.main-content {
  min-height: 2000px;
}

.backdrop {
  min-height: 1200px;
}
</style>
